import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "sourceInput",
    "dropzoneEl",
    "savedPreviewEl",
    "pendingPreviewEl",
    "pendingVisualEl",
    "pendingIconEl",
    "pendingNameEl",
    "pendingMetaEl",
    "fileFeedbackEl",
    "removeSourceInput",
    "removalNoticeEl",
    "statusHiddenInput",
    "actionBarEl",
    "footerSlotEl",
    "footerSentinelEl",
  ];
  static values = { invalid: Boolean };

  // Tracks form changes and the footer's original position.
  // Used by app/views/company_documents/_editor.html.erb.
  connect() {
    this.isDirty = this.invalidValue;
    this.isSaving = false;
    this.markedFields = new WeakSet();
    this.isFooterBelowView = false;

    // Reserve the footer's height when its buttons float.
    this.footerSlotElTarget.style.minHeight = `${Math.ceil(this.actionBarElTarget.getBoundingClientRect().height) + 1}px`;
    this.footerVisibilityChanged = this.footerVisibilityChanged.bind(this);
    this.footerObserver = new IntersectionObserver(this.footerVisibilityChanged, { threshold: [0, 1] });
    this.footerObserver.observe(this.footerSentinelElTarget);

    this.alignActionBar = this.alignActionBar.bind(this);
    window.addEventListener("resize", this.alignActionBar);
    this.syncContentRequirement();

    // Wait for the rich text editor to populate its hidden field.
    queueMicrotask(() => {
      this.initialFields = new Map(new FormData(this.element));
      this.initialValues = this.snapshot();
      this.element.classList.toggle("has-unsaved-changes", this.isDirty);
      this.showSaveNotice(this.isDirty ? this.unsavedNoticeState() : `status-${this.selectedStatus()}`);
      this.updateActionBar();
    });
  }

  // Serializes editable fields without transient authenticity tokens or file objects.
  // Called on connect and while pristine to detect the first change.
  snapshot() {
    return JSON.stringify([...new FormData(this.element)].filter(([key, value]) => key.startsWith("company_document[") && typeof value === "string"));
  }

  // Marks the form unsaved once, skipping comparisons after the first change.
  // Called by form input/change and company-document-editor:change via #refresh.
  refresh(event) {
    if (!this.initialValues) return;
    if (event) this.markChangedField(event);
    if (this.isDirty) {
      if (event?.target.name === "company_document[status]") this.showSaveNotice(this.unsavedNoticeState());
      return;
    }

    // SunEditor updates the hidden Markdown field after a delay, so its native input is the immediate signal.
    const isEditorInput = event?.type === "company-document-editor:input";
    this.isDirty = isEditorInput || this.snapshot() !== this.initialValues || (this.hasSourceInputTarget && this.sourceInputTarget.files.length > 0);
    this.element.classList.toggle("has-unsaved-changes", this.isDirty);
    if (this.isDirty) {
      this.showSaveNotice(this.unsavedNoticeState());
      this.updateActionBar();
    }
  }

  // Marks each edited field once without repeatedly comparing the entire form.
  // Called by refresh for named field input/change and the editor's change event.
  markChangedField(event) {
    // Editor events start on the wrapper, so find its saved Markdown field.
    const isEditorChange = event.type === "company-document-editor:change" || event.type === "company-document-editor:input";
    const input = isEditorChange ? event.target.querySelector('textarea[name="company_document[body_markdown]"]') : event.target;
    if (!input?.name?.startsWith("company_document[")) return;

    const field = input.closest(".company-document-field, .company-document-content");
    if (!field || this.markedFields.has(field)) return;
    // The editor's input event arrives before the hidden field is updated.
    if (event.type !== "company-document-editor:input" && (input.type === "file" ? !input.files.length : input.value === this.initialFields.get(input.name))) return;

    // Keep the marker until save or discard.
    this.markedFields.add(field);
    field.classList.add("has-unsaved-field");
  }

  // Reads the current status choice for selecting a pre-rendered notice.
  // Called by notice state selection after connect and form changes.
  selectedStatus() {
    return this.statusHiddenInputTarget.value || "draft";
  }

  // Selects the unsaved notice variant for the status currently in the form.
  // Called after edits and status changes before the form is submitted.
  unsavedNoticeState() {
    return `unsaved-${this.selectedStatus()}`;
  }

  // Shows one Rails-rendered notice variant without creating or changing markup.
  // Called on connect and during Turbo form submission states.
  showSaveNotice(state) {
    const notice = this.element.querySelector("#company-document-save-notice");
    if (!notice) return;

    notice.querySelectorAll("[data-company-document-notice-state]").forEach((message) => {
      message.hidden = message.dataset.companyDocumentNoticeState !== state;
    });
  }

  // Tracks when the footer's original position reaches the viewport bottom.
  // Called by IntersectionObserver watching footerSentinelEl.
  footerVisibilityChanged(entries) {
    const entry = entries[0];
    if (!entry) return;

    this.isFooterBelowView = entry.boundingClientRect.bottom > (entry.rootBounds?.bottom ?? window.innerHeight);
    this.updateActionBar();
  }

  // Floats the actions only when unsaved and their normal position is below view.
  // Called after dirty state changes and by footerVisibilityChanged.
  updateActionBar() {
    const shouldPin = this.isDirty && this.isFooterBelowView;
    if (shouldPin) this.alignActionBar();
    this.actionBarElTarget.classList.toggle("is-pinned", shouldPin);
  }

  // Aligns the pinned actions with the form's content on desktop and mobile.
  // Called by updateActionBar and when the viewport resizes.
  alignActionBar() {
    const bounds = this.element.getBoundingClientRect();
    this.actionBarElTarget.style.setProperty("--company-document-footer-left", `${bounds.left}px`);
    this.actionBarElTarget.style.setProperty("--company-document-footer-width", `${bounds.width}px`);
  }

  // Changes the hidden status value and immediately submits the document form.
  // Called by a status menu choice on click->company-document-form#changeStatus.
  changeStatus(event) {
    event.preventDefault();
    if (this.isSaving) return;

    this.statusHiddenInputTarget.value = event.currentTarget.dataset.status;
    this.refresh();
    this.element.requestSubmit();
  }

  // Opens the accessible file input from Browse or Replace buttons.
  // Called by click->company-document-form#openSourcePicker in the source area.
  openSourcePicker() {
    this.sourceInputTarget.click();
  }

  // Previews a supported local file without uploading it before the user saves.
  // Called by the source file input on change->company-document-form#preview.
  preview() {
    this.releasePreview();
    const file = this.sourceInputTarget.files[0];
    this.pendingVisualElTarget.replaceChildren();
    this.pendingPreviewElTarget.hidden = true;
    this.savedPreviewElTarget.hidden = !this.savedPreviewElTarget.querySelector(".company-document-file-heading") || this.removeSourceInputTarget.value === "true";
    this.dropzoneElTarget.hidden = !this.savedPreviewElTarget.hidden;
    this.fileFeedbackElTarget.textContent = "";
    if (!file) {
      this.syncContentRequirement();
      return;
    }

    const isPdf = file.type === "application/pdf";
    const isImage = ["image/jpeg", "image/png", "image/webp", "image/gif"].includes(file.type);
    if ((!isPdf && !isImage) || file.size > 25 * 1024 * 1024) {
      this.sourceInputTarget.value = "";
      this.fileFeedbackElTarget.textContent = "Choose a PDF, JPG, PNG, WebP or GIF no larger than 25 MB.";
      this.syncContentRequirement();
      this.refresh();
      return;
    }

    // Object URLs keep the pending preview local until the form is submitted.
    this.previewUrl = URL.createObjectURL(file);
    this.removeSourceInputTarget.value = "false";
    const preview = document.createElement(isPdf ? "iframe" : "img");
    preview.src = this.previewUrl;
    preview.className = isPdf ? "company-document-pdf-preview" : "company-document-image-preview";
    if (isPdf) preview.title = `PDF preview: ${file.name}`;
    else preview.alt = `Source preview: ${file.name}`;
    this.pendingVisualElTarget.append(preview);
    this.pendingIconElTarget.className = isPdf ? "bi bi-file-pdf" : "bx bx-image";
    this.pendingNameElTarget.textContent = file.name;
    this.pendingMetaElTarget.textContent = `${isPdf ? "PDF" : file.type.split("/")[1].toUpperCase()} · ${this.fileSize(file.size)} · Ready to save`;
    this.pendingPreviewElTarget.hidden = false;
    this.savedPreviewElTarget.hidden = true;
    this.dropzoneElTarget.hidden = true;
    this.removalNoticeElTarget.hidden = true;
    this.syncContentRequirement();
    this.refresh();
  }

  // Formats local attachment size for the pending file header.
  // Called by preview after validating a selected PDF or image.
  fileSize(bytes) {
    return bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
  }

  // Allows the upload area to accept a dragged file.
  // Called by the dropzone on dragenter->company-document-form#dragEnter.
  dragEnter(event) {
    event.preventDefault();
    this.dropzoneElTarget.classList.add("is-drag-over");
  }

  // Keeps the browser from opening a file during drag over the dropzone.
  // Called by the dropzone on dragover->company-document-form#dragOver.
  dragOver(event) {
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
  }

  // Removes the drag highlight when the pointer leaves the dropzone.
  // Called by the dropzone on dragleave->company-document-form#dragLeave.
  dragLeave(event) {
    if (!this.dropzoneElTarget.contains(event.relatedTarget)) this.dropzoneElTarget.classList.remove("is-drag-over");
  }

  // Passes a dropped file through the same validation and preview flow as browsing.
  // Called by the dropzone on drop->company-document-form#drop.
  drop(event) {
    event.preventDefault();
    this.dropzoneElTarget.classList.remove("is-drag-over");
    if (!event.dataTransfer.files.length) return;

    this.sourceInputTarget.files = event.dataTransfer.files;
    this.preview();
  }

  // Cancels a selected local file and restores a saved source when present.
  // Called by the pending file Remove button on click->company-document-form#clearSource.
  clearSource() {
    this.sourceInputTarget.value = "";
    this.releasePreview();
    this.pendingVisualElTarget.replaceChildren();
    this.pendingPreviewElTarget.hidden = true;
    this.savedPreviewElTarget.hidden = !this.savedPreviewElTarget.querySelector(".company-document-file-heading");
    this.dropzoneElTarget.hidden = !this.savedPreviewElTarget.hidden;
    this.syncContentRequirement();
    this.refresh();
  }

  // Requires written content only when the form has no attached or selected source.
  // Called after source selection or removal to match CompanyDocument validation.
  syncContentRequirement() {
    const body = this.element.querySelector("textarea[name='company_document[body_markdown]']");
    if (!body) return;

    const hasSavedSource = this.hasSavedPreviewElTarget && !!this.savedPreviewElTarget.querySelector(".company-document-file-heading");
    const hasPendingSource = this.hasSourceInputTarget && this.sourceInputTarget.files.length > 0;
    const isRemovingSource = this.hasRemoveSourceInputTarget && this.removeSourceInputTarget.value === "true";
    body.required = !hasPendingSource && (!hasSavedSource || isRemovingSource);
  }

  // Stages source removal without deleting the company document or its notes.
  // Called by the source menu button on click->company-document-form#removeSource.
  removeSource() {
    // Keep the source attached until the user saves a valid document.
    this.removeSourceInputTarget.value = "true";
    this.removeSourceInputTarget.closest(".company-document-source").classList.add("has-unsaved-field");
    this.savedPreviewElTarget.hidden = true;
    this.dropzoneElTarget.hidden = false;
    this.removalNoticeElTarget.hidden = false;
    this.syncContentRequirement();
    this.removalNoticeElTarget.querySelector("button").focus();
    this.refresh();
  }

  // Cancels the pending removal and restores the original preview.
  // Called by Undo removal on click->company-document-form#undoRemoval.
  undoRemoval() {
    this.removeSourceInputTarget.value = "false";
    this.savedPreviewElTarget.hidden = false;
    this.dropzoneElTarget.hidden = true;
    this.removalNoticeElTarget.hidden = true;
    this.syncContentRequirement();
    this.savedPreviewElTarget.querySelector(".company-document-more-btn").focus();
    this.refresh();
  }

  // Confirms discarding edits before Turbo reloads the editor frame.
  // Called by a.company-document-discard-link on click->company-document-form#confirmDiscard.
  confirmDiscard(event) {
    if (this.isSaving || !window.confirm("Discard all unsaved changes? This cannot be undone.")) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }

  // Shows a pending state while Turbo sends the form.
  // Called by the form's turbo:submit-start->company-document-form#saving action.
  saving() {
    this.isSaving = true;
    this.showSaveNotice("saving");
  }

  // Restores the unsaved indicator after a network or validation failure.
  // Called by the form's turbo:submit-end->company-document-form#submitted action.
  submitted(event) {
    this.isSaving = false;
    if (event.detail.success) {
      this.isDirty = false;
      this.initialFields = new Map(new FormData(this.element));
      this.initialValues = this.snapshot();
      this.markedFields = new WeakSet();
      this.element.querySelectorAll(".has-unsaved-field").forEach((field) => field.classList.remove("has-unsaved-field"));
      this.showSaveNotice(`status-${this.selectedStatus()}`);
    } else {
      this.showSaveNotice("error");
    }

    this.element.classList.toggle("has-unsaved-changes", this.isDirty);
    this.updateActionBar();
  }

  // Asks the browser to protect pending edits on reload or tab close.
  // Called by beforeunload@window->company-document-form#leavePage.
  leavePage(event) {
    if (!this.isDirty || this.isSaving) return;
    event.preventDefault();
    event.returnValue = "";
  }

  // Releases the browser's local file reference after replacement or navigation.
  // Called by preview and disconnect to avoid retaining large files in memory.
  releasePreview() {
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
    this.previewUrl = null;
  }

  // Cleans up file previews when Turbo removes the form.
  // Called by Stimulus on controller disconnect.
  disconnect() {
    this.releasePreview();
    this.footerObserver?.disconnect();
    window.removeEventListener("resize", this.alignActionBar);
  }
}
